import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatPHP, formatDate, formatErrorDetail, API_BASE } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import StatusBadge from "@/components/app/StatusBadge";
import EmployeePicker from "@/components/app/EmployeePicker";
import { Play, Send, ShieldCheck, Lock, Unlock, FileSpreadsheet, ArrowLeft, AlertTriangle, CheckCircle2, RefreshCcw, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import PayslipHours from "@/components/app/PayslipHours";
import PayslipDetail from "@/components/app/PayslipDetail";
import { downloadPayslipPdf, pdfErrorMessage } from "@/lib/payslipPdf";

export default function PayrollRunDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [run, setRun] = useState(null);
    const [items, setItems] = useState([]);
    const [busy, setBusy] = useState(false);
    const [unlockOpen, setUnlockOpen] = useState(false);
    const [unlockReason, setUnlockReason] = useState("");
    const [validateOpen, setValidateOpen] = useState(false);
    const [validation, setValidation] = useState(null);
    const [riskOpen, setRiskOpen] = useState(false);
    const [risk, setRisk] = useState(null);
    const [computeOverrideOpen, setComputeOverrideOpen] = useState(false);
    const [computeBlockMessage, setComputeBlockMessage] = useState("");
    const [overrideReason, setOverrideReason] = useState("");
    const [retroOpen, setRetroOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [retroForm, setRetroForm] = useState({ employee_id: "", component: "salary", effective_date: "", old_monthly: "", new_monthly: "", periods: 1, reason: "" });
    const [retroPreview, setRetroPreview] = useState(null);
    // The payslip of one item, read by the payroll side (the same hours block the employee sees).
    const [slip, setSlip] = useState(null);
    const openSlip = async (it) => {
        try {
            const { data } = await api.get(`/payroll/items/${it.id}/payslip`);
            setSlip(data);
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not open the payslip");
        }
    };

    const load = async () => {
        const { data } = await api.get(`/payroll/runs/${id}`);
        setRun(data.run); setItems(data.items || []);
    };
    useEffect(() => { load(); }, [id]);

    const action = async (path, body = {}) => {
        setBusy(true);
        try {
            await api.post(`/payroll/runs/${id}/${path}`, body);
            toast.success("Done");
            load();
        } catch (e) {
            const msg = formatErrorDetail(e.response?.data?.detail) || "Failed";
            if (path === "compute" && e.response?.status === 409 && String(msg).toLowerCase().includes("missing attendance")) {
                setComputeBlockMessage(msg);
                setComputeOverrideOpen(true);
            } else {
                toast.error(msg);
            }
        }
        finally { setBusy(false); }
    };

    // A cutoff is a Basic + Allowance pair; the server deletes both, and only
    // while the cutoff is still draft or computed.
    const deleteCutoff = async () => {
        setBusy(true);
        try {
            await api.delete(`/payroll/runs/${id}`);
            toast.success("Cutoff deleted");
            navigate("/payroll/runs");
        } catch (e) {
            toast.error(formatErrorDetail(e.response?.data?.detail) || "Could not delete the cutoff");
        } finally { setBusy(false); setDeleteOpen(false); }
    };

    const exportRun = async () => {
        try {
            const r = await api.get(`/payroll/runs/${id}/export`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data]));
            const a = document.createElement("a");
            a.href = url; a.download = `payroll_${run.period_start}_${run.period_end}.xlsx`;
            a.click(); URL.revokeObjectURL(url);
        } catch { toast.error("Export failed"); }
    };

    const validate = async () => {
        try {
            const r = await api.post(`/compliance/runs/${id}/validate`);
            setValidation(r.data); setValidateOpen(true);
        } catch (e) { toast.error("Validation failed"); }
    };

    const snapshot = async () => {
        try {
            await api.post(`/compliance/runs/${id}/snapshot`);
            toast.success("Compliance snapshot generated");
        } catch { toast.error("Failed to snapshot"); }
    };

    const checkRisk = async () => {
        try {
            const r = await api.get(`/insights/payroll-risk/${id}`);
            setRisk(r.data); setRiskOpen(true);
        } catch { toast.error("Risk check failed"); }
    };

    const retroPayload = () => ({
        old_monthly: Number(retroForm.old_monthly) || 0,
        new_monthly: Number(retroForm.new_monthly) || 0,
        frequency: run?.frequency || "semi_monthly",
        affected_periods: Array.from({ length: Math.max(1, Number(retroForm.periods) || 1) }, (_, i) => `P${i + 1}`),
    });

    const previewRetro = async () => {
        try {
            const r = await api.post(`/payroll/retro/preview`, retroPayload());
            setRetroPreview(r.data);
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Preview failed"); }
    };

    const submitRetro = async () => {
        if (!retroForm.employee_id) return toast.error("Select an employee");
        if (!retroForm.effective_date) return toast.error("Effective date is required");
        if (retroForm.reason.trim().length < 5) return toast.error("A reason of at least 5 characters is required");
        try {
            await api.post(`/payroll/retro/runs/${id}`, {
                ...retroPayload(),
                employee_id: retroForm.employee_id,
                component: retroForm.component,
                effective_date: retroForm.effective_date,
                reason: retroForm.reason,
            });
            toast.success("Retro adjustment created (pending approval)");
            setRetroOpen(false);
            setRetroPreview(null);
            setRetroForm({ employee_id: "", component: "salary", effective_date: "", old_monthly: "", new_monthly: "", periods: 1, reason: "" });
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to create retro adjustment"); }
    };

    if (!run) return <div className="text-sm text-muted-foreground">Loading…</div>;

    return (
        <div>
            <Link to="/payroll/runs" className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-2" data-testid="back-to-runs"><ArrowLeft className="size-3" /> Back to payroll runs</Link>
            <PageHeader
                title={`Payroll Run · ${run.period_start} → ${run.period_end}`}
                subtitle={`Status: ${run.status} · ${run.frequency?.replace("_", " ")} · ${run.employee_count} employees`}
                actions={
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge value={run.status} />
                        {run.locked && <StatusBadge value="locked" />}
                        {!run.locked && <button disabled={busy} onClick={() => action("compute")} className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-1" data-testid="compute-btn"><Play className="size-4" /> Compute</button>}
                        {!run.locked && run.status === "computed" && <button disabled={busy} onClick={() => action("submit")} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="submit-btn"><Send className="size-4" /> Submit for Review</button>}
                        {!run.locked && run.status === "submitted" && <button disabled={busy} onClick={() => action("approve-accounting")} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="approve-acct-btn"><CheckCircle2 className="size-4" /> Approve (Accounting)</button>}
                        {!run.locked && run.status === "approved_acct" && <button disabled={busy} onClick={() => action("approve-gm")} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="approve-gm-btn"><CheckCircle2 className="size-4" /> Approve (GM)</button>}
                        <button onClick={validate} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="validate-btn"><ShieldCheck className="size-4" /> Validate</button>
                        <button onClick={checkRisk} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="risk-check-btn"><AlertTriangle className="size-4" /> Risk Check</button>
                        {!run.locked && <button onClick={() => setRetroOpen(true)} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="retro-btn"><RefreshCcw className="size-4" /> Retro</button>}
                        {!run.locked && <button disabled={busy} onClick={() => action("lock")} className="h-9 px-3 rounded-md bg-gray-700 text-white text-sm flex items-center gap-1" data-testid="lock-btn"><Lock className="size-4" /> Lock</button>}
                        {run.locked && <button onClick={() => setUnlockOpen(true)} className="h-9 px-3 rounded-md border border-destructive text-destructive text-sm flex items-center gap-1" data-testid="unlock-btn"><Unlock className="size-4" /> Unlock</button>}
                        {run.locked && <button onClick={snapshot} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="snapshot-btn"><ShieldCheck className="size-4" /> Generate Snapshot</button>}
                        <button onClick={exportRun} className="h-9 px-3 rounded-md border border-border bg-background text-sm flex items-center gap-1" data-testid="export-btn"><FileSpreadsheet className="size-4" /> Export Excel</button>
                        {!run.locked && ["draft", "computed"].includes(run.status) && <button disabled={busy} onClick={() => setDeleteOpen(true)} className="h-9 px-3 rounded-md border border-destructive text-destructive text-sm flex items-center gap-1" data-testid="delete-cutoff-btn"><Trash2 className="size-4" /> Delete cutoff</button>}
                    </div>
                }
            />

            {(run.anomalies || []).length > 0 && (
                <Surface className="p-3 mb-3 border-amber-300 bg-amber-50/70" data-testid="anomaly-banner">
                    <div className="text-sm font-semibold text-amber-900 mb-1.5">
                        ⚠ {run.anomalies.length} anomal{run.anomalies.length > 1 ? "ies" : "y"} detected on the last compute — review before approving
                    </div>
                    <div className="space-y-1">
                        {run.anomalies.map((a, i) => (
                            <div key={i} className="text-xs flex items-start gap-1.5">
                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold shrink-0 ${a.severity === "error" ? "bg-red-600 text-white" : "bg-amber-200 text-amber-900"}`}>{a.severity}</span>
                                <span><b>{a.employee_name}</b> — {a.message} <span className="text-muted-foreground">({a.code})</span></span>
                            </div>
                        ))}
                    </div>
                </Surface>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <Stat label="Employees" value={run.employee_count} />
                <Stat label="Gross" value={formatPHP(run.total_gross)} />
                <Stat label="Deductions" value={formatPHP(run.total_deductions)} />
                <Stat label="Net Pay" value={formatPHP(run.total_net)} tone="primary" />
            </div>

            {/* Approval Trail */}
            <Surface className="p-4 mb-3">
                <h3 className="font-display font-semibold mb-2 text-sm">Approval Trail</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                    <div><div className="text-muted-foreground">Submitted</div><div>{run.submitted_at ? `${formatDate(run.submitted_at)} · ${run.submitted_by?.slice(0,8)}` : "—"}</div></div>
                    <div><div className="text-muted-foreground">Approved (Accounting)</div><div>{run.approved_acct_at ? `${formatDate(run.approved_acct_at)}` : "—"}</div></div>
                    <div><div className="text-muted-foreground">Approved (GM)</div><div>{run.approved_gm_at ? `${formatDate(run.approved_gm_at)}` : "—"}</div></div>
                    <div><div className="text-muted-foreground">Locked</div><div>{run.locked_at ? formatDate(run.locked_at) : "—"} {run.unlock_reason && <span className="text-destructive">(Unlocked: {run.unlock_reason})</span>}</div></div>
                </div>
            </Surface>

            <TableWrap>
                <thead>
                    <tr>
                        <th>Emp No</th>
                        <th>Name</th>
                        <th className="num">Basic</th>
                        <th className="num">OT</th>
                        <th className="num">Allow.</th>
                        <th className="num">Hazard</th>
                        <th className="num">Gross</th>
                        <th className="num">SSS</th>
                        <th className="num">PhilHealth</th>
                        <th className="num">Pag-IBIG</th>
                        <th className="num">WHT</th>
                        <th className="num">Net</th>
                        <th>Payslip</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map((it) => (
                        <tr key={it.id} data-testid={`item-${it.employee_no}`}>
                            <td className="font-mono">{it.employee_no}</td>
                            <td>{it.employee_name}</td>
                            <td className="num">{formatPHP(it.basic_pay)}</td>
                            <td className="num">{formatPHP(it.ot_pay)}</td>
                            <td className="num">{formatPHP((it.allowances_taxable || 0) + (it.allowances_nontaxable || 0))}</td>
                            <td className="num">{formatPHP(it.hazard_pay || 0)}</td>
                            <td className="num font-medium">{formatPHP(it.gross_pay)}</td>
                            <td className="num">{formatPHP(it.sss_ee)}</td>
                            <td className="num">{formatPHP(it.philhealth_ee)}</td>
                            <td className="num">{formatPHP(it.pagibig_ee)}</td>
                            <td className="num">{formatPHP(it.withholding_tax)}</td>
                            <td className="num font-bold">{formatPHP(it.net_pay)}</td>
                            <td><button type="button" className="text-xs font-semibold underline" onClick={() => openSlip(it)} data-testid={`slip-${it.employee_no}`}>Hours &amp; payslip</button></td>
                        </tr>
                    ))}
                    {!items.length && <tr><td colSpan={13}><Empty title="No payroll items" subtitle="Click Compute to populate this run." /></td></tr>}
                </tbody>
            </TableWrap>

            {/* Payslip with the HOURS block */}
            <Dialog open={!!slip} onOpenChange={(o) => !o && setSlip(null)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle>Payslip: {slip?.payroll_item?.employee_name} ({slip?.payroll_item?.employee_no})</DialogTitle></DialogHeader>
                    {slip && (
                        <div className="space-y-4 text-sm">
                            <div className="text-xs text-muted-foreground">{slip.payroll_run?.period_start} to {slip.payroll_run?.period_end} &middot; gross {formatPHP(slip.payroll_item?.gross_pay)} &middot; net {formatPHP(slip.payroll_item?.net_pay)}</div>
                            <PayslipDetail detail={slip.detail} />
                            <PayslipHours block={slip.hours_block} />
                            <button type="button" className="rounded-md border border-border px-3 py-2 text-sm font-semibold" data-testid="slip-pdf"
                                onClick={async () => { try { await downloadPayslipPdf(slip.payroll_item.id, `/payroll/items/${slip.payroll_item.id}/payslip/pdf`); } catch (e) { toast.error(await pdfErrorMessage(e)); } }}>
                                Download PDF{slip.company?.payslip_password_protect ? " (password protected)" : ""}
                            </button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Missing attendance override dialog */}
            <Dialog open={computeOverrideOpen} onOpenChange={setComputeOverrideOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Payroll Compute Blocked</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                            {computeBlockMessage || "This payroll run has employees with missing attendance data."}
                        </div>
                        <p className="text-sm text-muted-foreground">
                            This override is high-risk. Use it only for approved fixed-salary or manual payroll cases.
                            The reason will be saved in the payroll audit trail.
                        </p>
                        <div>
                            <Label>Override Reason</Label>
                            <textarea
                                className="w-full p-2 rounded-md border border-border text-sm"
                                rows={4}
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder="Example: Approved fixed monthly payroll override for salaried employees; attendance will be reconciled separately."
                                data-testid="missing-attendance-override-reason"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => {
                                setComputeOverrideOpen(false);
                                setOverrideReason("");
                            }}
                            className="h-9 px-3 rounded-md border border-border bg-background text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            disabled={busy || overrideReason.trim().length < 10}
                            onClick={async () => {
                                await action("compute", {
                                    allow_missing_attendance: true,
                                    override_reason: overrideReason,
                                });
                                setComputeOverrideOpen(false);
                                setOverrideReason("");
                            }}
                            className="h-9 px-3 rounded-md bg-destructive text-white text-sm disabled:opacity-50"
                            data-testid="confirm-missing-attendance-override"
                        >
                            Override and Compute
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Delete this cutoff?</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        This deletes the {run.period_start} to {run.period_end} cutoff: the Basic run and its Allowance run, with any computed figures.
                        Employees, attendance and leave are not touched. You can create the cutoff again afterwards.
                    </p>
                    <DialogFooter>
                        <button type="button" onClick={() => setDeleteOpen(false)} className="h-9 px-3 rounded-md border border-border bg-background text-sm">Keep it</button>
                        <button disabled={busy} onClick={deleteCutoff} className="h-9 px-3 rounded-md bg-destructive text-white text-sm disabled:opacity-50" data-testid="confirm-delete-cutoff">Delete cutoff</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Retro adjustment dialog */}
            <Dialog open={retroOpen} onOpenChange={(o) => { setRetroOpen(o); if (!o) setRetroPreview(null); }}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Retroactive Pay Adjustment</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        Compute the per-period difference for an effective-dated change and record it as a
                        pending-approval adjustment on this run. Positive totals are owed to the employee; negative totals are recoverable.
                    </p>
                    <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="col-span-2">
                            <Label>Employee</Label>
                            <EmployeePicker value={retroForm.employee_id} onChange={(v) => setRetroForm({ ...retroForm, employee_id: v })}
                                options={items.map((it) => ({ id: it.employee_id, label: `${it.employee_no} · ${it.employee_name}` }))}
                                placeholder="Search employee in this run…" testId="retro-employee" />
                        </div>
                        <div>
                            <Label>Component</Label>
                            <select className="w-full h-10 px-3 rounded-md border border-border" value={retroForm.component} onChange={(e) => setRetroForm({ ...retroForm, component: e.target.value })} data-testid="retro-component">
                                <option value="salary">Salary</option>
                                <option value="allowance">Allowance</option>
                                <option value="overtime">Overtime</option>
                                <option value="holiday">Holiday</option>
                                <option value="deduction">Deduction</option>
                                <option value="government_deduction">Government deduction</option>
                                <option value="tax">Tax</option>
                            </select>
                        </div>
                        <div><Label>Effective Date</Label><Input type="date" value={retroForm.effective_date} onChange={(e) => setRetroForm({ ...retroForm, effective_date: e.target.value })} data-testid="retro-effective-date" /></div>
                        <div><Label>Old Monthly Amount</Label><Input type="number" value={retroForm.old_monthly} onChange={(e) => { setRetroForm({ ...retroForm, old_monthly: e.target.value }); setRetroPreview(null); }} data-testid="retro-old" /></div>
                        <div><Label>New Monthly Amount</Label><Input type="number" value={retroForm.new_monthly} onChange={(e) => { setRetroForm({ ...retroForm, new_monthly: e.target.value }); setRetroPreview(null); }} data-testid="retro-new" /></div>
                        <div><Label>Affected Periods</Label><Input type="number" min={1} value={retroForm.periods} onChange={(e) => { setRetroForm({ ...retroForm, periods: e.target.value }); setRetroPreview(null); }} data-testid="retro-periods" /></div>
                        <div className="col-span-2"><Label>Reason</Label><textarea className="w-full p-2 rounded-md border border-border text-sm" rows={2} value={retroForm.reason} onChange={(e) => setRetroForm({ ...retroForm, reason: e.target.value })} placeholder="e.g. Approved salary increase back-dated to effective date" data-testid="retro-reason" /></div>
                    </div>
                    {retroPreview && (
                        <div className="rounded-md border border-border bg-secondary/40 p-3 text-sm mt-1" data-testid="retro-preview">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">{retroPreview.period_count} period(s) · total difference</span>
                                <span className={`font-bold ${retroPreview.total_delta < 0 ? "text-destructive" : "text-emerald-600"}`}>{formatPHP(retroPreview.total_delta)}</span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <button onClick={previewRetro} className="h-9 px-3 rounded-md border border-border bg-background text-sm" data-testid="retro-preview-btn">Preview Difference</button>
                        <button onClick={submitRetro} className="h-9 px-3 rounded-md bg-primary text-white text-sm" data-testid="retro-submit-btn">Create Adjustment</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Unlock dialog */}
            <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Unlock Payroll Run</DialogTitle></DialogHeader>
                    <p className="text-sm text-muted-foreground">Unlocking a locked payroll run is an audited action. Provide a reason for the change.</p>
                    <div>
                        <Label>Unlock Reason</Label>
                        <textarea className="w-full p-2 rounded-md border border-border text-sm" rows={3} value={unlockReason} onChange={(e) => setUnlockReason(e.target.value)} data-testid="unlock-reason-input" />
                    </div>
                    <DialogFooter>
                        <button onClick={async () => { await action("unlock", { reason: unlockReason }); setUnlockOpen(false); setUnlockReason(""); }} className="h-9 px-3 rounded-md bg-destructive text-white text-sm" data-testid="confirm-unlock-btn">Unlock</button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Validation results dialog */}
            <Dialog open={validateOpen} onOpenChange={setValidateOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Validation Results</DialogTitle></DialogHeader>
                    {validation && (
                        <div className="space-y-3 max-h-[60vh] overflow-auto">
                            <div className="flex gap-2 text-sm">
                                <span className="badge badge-rejected">{validation.errors?.length || 0} errors</span>
                                <span className="badge badge-draft">{validation.warnings?.length || 0} warnings</span>
                            </div>
                            {(validation.errors || []).map((e, i) => (
                                <div key={"e" + i} className="flex items-start gap-2 text-sm border-l-2 border-destructive pl-2"><AlertTriangle className="size-4 text-destructive mt-0.5" /><div><div className="font-medium">{e.code}</div><div className="text-muted-foreground">{e.message}</div></div></div>
                            ))}
                            {(validation.warnings || []).map((w, i) => (
                                <div key={"w" + i} className="flex items-start gap-2 text-sm border-l-2 border-yellow-500 pl-2"><AlertTriangle className="size-4 text-yellow-600 mt-0.5" /><div><div className="font-medium">{w.code}</div><div className="text-muted-foreground">{w.message}</div></div></div>
                            ))}
                            {!validation.errors?.length && !validation.warnings?.length && (
                                <div className="text-sm text-emerald-600 flex items-center gap-2"><CheckCircle2 className="size-4" /> All checks passed. Safe to export.</div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Risk Check dialog */}
            <Dialog open={riskOpen} onOpenChange={setRiskOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Payroll Risk Check</DialogTitle></DialogHeader>
                    {risk && (
                        <div className="space-y-3 max-h-[60vh] overflow-auto">
                            <div className="flex gap-2 text-sm">
                                <span className="badge badge-rejected" data-testid="risk-critical-count">{risk.critical?.length || 0} critical</span>
                                <span className="badge badge-draft" data-testid="risk-warning-count">{risk.warnings?.length || 0} warnings</span>
                            </div>
                            <p className="text-xs text-muted-foreground">These checks do not block payroll. Review and confirm before locking. Previous run net: {formatPHP(risk.prev_net || 0)} · current: {formatPHP(risk.current_net || 0)}.</p>
                            {(risk.critical || []).map((e, i) => (
                                <div key={"rc" + i} className="flex items-start gap-2 text-sm border-l-2 border-destructive pl-2"><AlertTriangle className="size-4 text-destructive mt-0.5" /><div><div className="font-medium">{e.code}</div><div className="text-muted-foreground">{e.message}</div></div></div>
                            ))}
                            {(risk.warnings || []).map((w, i) => (
                                <div key={"rw" + i} className="flex items-start gap-2 text-sm border-l-2 border-yellow-500 pl-2"><AlertTriangle className="size-4 text-yellow-600 mt-0.5" /><div><div className="font-medium">{w.code}</div><div className="text-muted-foreground">{w.message}</div></div></div>
                            ))}
                            {!risk.critical?.length && !risk.warnings?.length && (
                                <div className="text-sm text-emerald-600 flex items-center gap-2"><CheckCircle2 className="size-4" /> No risks detected. Safe to proceed.</div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
